import gql from "graphql-tag";
import type { ApolloClient } from "@apollo/client";

import type {
  CreateDisbursementRequestMutation,
  CreateDisbursementRequestMutationVariables,
  MakeWithdrawalMutation,
  MakeWithdrawalMutationVariables,
} from "types/graphql";

/*
  Request to initiate a withdrawal from users IRA
*/
export function makeWithdrawal({
  client,
  iraAccountId,
  amount,
  reason,
  isFullDisbursement,
}: {
  client: ApolloClient<object>;
  iraAccountId: string;
  amount: number;
  reason: string;
  isFullDisbursement: boolean;
}): Promise<MakeWithdrawalMutation> {
  return new Promise((resolve, reject) => {
    const MAKE_WITHDRAWAL = gql`
      mutation makeWithdrawal($withdrawalParams: withdrawalRequest) {
        makeWithdrawal(withdrawalParams: $withdrawalParams)
      }
    `;

    const withdrawalParams = {
      amount,
      isFullDisbursement,
      iraAccountId,
      reason,
    };

    const result = client.mutate<
      MakeWithdrawalMutation,
      MakeWithdrawalMutationVariables
    >({
      mutation: MAKE_WITHDRAWAL,
      variables: {
        withdrawalParams,
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

function createDisbursementRequest(
  client: ApolloClient<object>,
  disbursementInput: CreateDisbursementRequestMutationVariables["input"]
): Promise<
  CreateDisbursementRequestMutation["createDisbursementRequest"] | null
> {
  return new Promise((resolve, reject) => {
    const CREATE_DISBURSEMENT = gql`
      mutation createDisbursementRequest($input: DisbursementInput!) {
        createDisbursementRequest(input: $input)
      }
    `;

    const result = client.mutate<
      CreateDisbursementRequestMutation,
      CreateDisbursementRequestMutationVariables
    >({
      mutation: CREATE_DISBURSEMENT,
      variables: {
        input: disbursementInput,
      },
    });

    result.then(
      (data) => {
        resolve(data.data?.createDisbursementRequest ?? null);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

export const withdrawalService = {
  makeWithdrawal,
  createDisbursementRequest,
};
